import { PriceDropSuggestionsCard } from '@/components/cards/price-drop-suggestions-card';
import { RecentSalesCard } from '@/components/cards/recent-sales-card';
import { StatCard } from '@/components/cards/stat-card';
import { InventoryImportDialog } from '@/components/inventory/inventory-import-dialog';
import { Layout } from '@/components/layouts/layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useInventoryAll } from '@/hooks/use-inventory';
import { useSession } from '@/lib/auth-client';
import {
    buildPriceDropSuggestions,
    normalizeValue,
    sumBy,
} from '@/lib/dashboard-metrics';
import {
    formatCurrency,
    formatRelativeDate,
    parseDate,
} from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { InventoryRecord } from '@/types/inventory';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
    AlertCircle,
    AlertTriangleIcon,
    DollarSign,
    Plus,
    TrendingUp,
} from 'lucide-react';

export const Route = createFileRoute('/dashboard')({
    component: Dashboard,
});

function getDaysSince(value: string | null | undefined): number | null {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
}

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

function Dashboard() {
    const { data, isSuccess, isLoading, isError, error } = useInventoryAll();
    const { data: session } = useSession();
    const firstName = (
        (session?.user?.user_metadata?.full_name as string | undefined) ?? ''
    )
        .trim()
        .split(' ')[0];
    const rows = data?.list ?? [];

    const soldItems = rows.filter(
        (item) => normalizeValue(item['Item Status']) === 'sold',
    );
    const listedItems = rows.filter(
        (item) => normalizeValue(item['Item Status']) === 'listed',
    );
    const unlistedItems = rows.filter(
        (item) => normalizeValue(item['Item Status']) === 'unlisted',
    );
    const staleListedItems = listedItems.filter((item) => {
        const days = getDaysSince(item['Date Listed']);
        return days !== null && days >= 45;
    });

    function getNumber(value: unknown) {
        return typeof value === 'number' && Number.isFinite(value) ? value : 0;
    }

    function getSoldFees(item: InventoryRecord) {
        return getNumber(item['Platform Fees']) || 0;
    }

    function getNetProfit(item: InventoryRecord) {
        const precomputed = item['Net Profit'];
        if (typeof precomputed === 'number' && Number.isFinite(precomputed)) {
            return precomputed;
        }
        return (
            getNumber(item['Sale Price']) -
            getNumber(item['Cost']) -
            getSoldFees(item)
        );
    }

    const soldNetProfit = sumBy(soldItems, (item) => getNetProfit(item));

    const currentYear = new Date().getFullYear();
    const THRESHOLD_BY_YEAR: Record<number, number> = {
        2023: 20000,
        2024: 5000,
        2025: 2500,
    };
    const threshold1099k = THRESHOLD_BY_YEAR[currentYear] ?? 600;

    const ytdGrossSales = soldItems.reduce((sum, item) => {
        const dateSold = item['Date Sold'];
        if (!dateSold) return sum;
        const d = new Date(dateSold);
        if (isNaN(d.getTime()) || d.getFullYear() !== currentYear) return sum;
        return sum + getNumber(item['Sale Price']) + getNumber(item['Shipping Charged']);
    }, 0);
    const thresholdPct = Math.min(ytdGrossSales / threshold1099k, 1);

    const priceDropSuggestions = buildPriceDropSuggestions(rows);

    const recentSales = soldItems
        .filter((item) => Boolean(item['Date Sold']))
        .sort((a, b) => {
            const aDate = parseDate(a['Date Sold']);
            const bDate = parseDate(b['Date Sold']);
            return bDate.getTime() - aDate.getTime();
        })
        .slice(0, 6)
        .map((item) => ({
            id: item.Id,
            title: `${item.Brand || 'Unknown'} ${item.Description || ''}`.trim(),
            subtitle: `${item['Sold Platform'] || 'Unknown platform'} • ${formatRelativeDate(item['Date Sold'])}`,
            price: formatCurrency(item['Sale Price']),
            profit: formatCurrency(item['Net Profit']),
            imageUrls: item['Image URLs'],
            category: item.Category,
        }));

    return (
        <Layout>
            {isSuccess && rows?.length <= 0 && (
                <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6">
                    <Alert>
                        <AlertTriangleIcon />
                        <AlertTitle>
                            You don't have any inventory yet!
                        </AlertTitle>
                        <AlertDescription>
                            <span>
                                Let's get started by adding items to your
                                inventory.
                            </span>
                            <div className="flex gap-2 mt-2">
                                <Link to="/add" className="hover:underline">
                                    <Button>
                                        <Plus /> Add Item
                                    </Button>
                                </Link>
                                <InventoryImportDialog />
                            </div>
                        </AlertDescription>
                    </Alert>
                </div>
            )}

            <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="">
                        <p className="text-sm text-muted-foreground uppercase tracking-wide">
                            {greeting()}
                        </p>
                        <h1 className="text-4xl font-semibold mt-0.5">
                            {firstName || 'Welcome back'}
                        </h1>
                    </div>
                    <div className="hidden sm:flex flex-wrap items-center gap-2">
                        <Button asChild variant="outline">
                            <Link to="/inventory" search={{ q: '' }}>
                                View Inventory
                            </Link>
                        </Button>
                        <Button asChild>
                            <Link to="/add">Add Item</Link>
                        </Button>
                    </div>
                </div>

                {isLoading && <DashboardSkeleton />}

                {isError && (
                    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-sm text-destructive">
                        {(error as Error).message}
                    </div>
                )}

                {!isLoading && !isError && (
                    <>
                        <div className="grid gap-4 sm:grid-cols-3">
                            <StatCard
                                title="Net Profit"
                                value={formatCurrency(soldNetProfit)}
                                subtitle={`From ${soldItems.length} sold items`}
                                icon={
                                    <DollarSign className="size-6 stroke-green-500" />
                                }
                            />
                            <StatCard
                                title="Needs Attention"
                                value={staleListedItems.length.toString()}
                                subtitle={`Listed 45+ days · ${unlistedItems.length} unlisted`}
                                icon={
                                    <AlertCircle className="size-6 stroke-amber-500" />
                                }
                            />
                            <StatCard
                                title="Recent Sales"
                                value={
                                    recentSales.length > 0
                                        ? recentSales.length.toString()
                                        : '0'
                                }
                                subtitle="Last 6 sold items"
                                icon={
                                    <TrendingUp className="size-6 stroke-blue-500" />
                                }
                            />
                        </div>

                        {ytdGrossSales > 0 && (
                            <div className={cn(
                                'rounded-lg border p-4',
                                thresholdPct >= 1
                                    ? 'border-red-500/50 bg-red-500/10'
                                    : thresholdPct >= 0.8
                                      ? 'border-amber-500/50 bg-amber-500/10'
                                      : 'border-border bg-card',
                            )}>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-medium">
                                            {thresholdPct >= 1
                                                ? `1099-K threshold reached (${currentYear})`
                                                : thresholdPct >= 0.8
                                                  ? `Approaching 1099-K threshold (${currentYear})`
                                                  : `1099-K tracker (${currentYear})`}
                                        </p>
                                        <p className="mt-0.5 text-xs text-muted-foreground">
                                            {formatCurrency(ytdGrossSales)} of {formatCurrency(threshold1099k)} gross sales threshold
                                            {thresholdPct >= 1 && ' — platforms are required to report your sales to the IRS'}
                                        </p>
                                    </div>
                                    <p className={cn(
                                        'text-sm font-semibold tabular-nums shrink-0',
                                        thresholdPct >= 1 ? 'text-red-500' : thresholdPct >= 0.8 ? 'text-amber-500' : 'text-foreground',
                                    )}>
                                        {(thresholdPct * 100).toFixed(0)}%
                                    </p>
                                </div>
                                <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
                                    <div
                                        className={cn(
                                            'h-full rounded-full transition-all',
                                            thresholdPct >= 1 ? 'bg-red-500' : thresholdPct >= 0.8 ? 'bg-amber-500' : 'bg-primary',
                                        )}
                                        style={{ width: `${Math.min(thresholdPct * 100, 100)}%` }}
                                    />
                                </div>
                                <p className="mt-2 text-xs text-muted-foreground opacity-60">
                                    Tracks gross payments received (sale price + shipping). Thresholds: $5,000 (2024), $2,500 (2025), $600 (2026+). For informational purposes only — consult a tax professional.
                                </p>
                            </div>
                        )}

                        <div className="grid gap-6 lg:grid-cols-3">
                            <div className="lg:col-span-2">
                                <PriceDropSuggestionsCard
                                    items={priceDropSuggestions}
                                />
                            </div>
                            <RecentSalesCard
                                items={recentSales}
                                className="lg:col-span-2"
                            />
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}

function DashboardSkeleton() {
    return (
        <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-3">
                {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-28 w-full" />
                ))}
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
                <Skeleton className="h-64 w-full lg:col-span-2" />
                <Skeleton className="h-64 w-full" />
            </div>
        </div>
    );
}
